import type { GscMetrics } from '@marketing-hub/shared';
import { defaultKpiRange, isoDateUtc } from './kpiRollup';

export type GscSnapshot = { snapshot_date: string; metrics: GscMetrics };

export { defaultKpiRange, isoDateUtc };

export function sumGscLastDays(snapshots: GscSnapshot[], days: number): GscMetrics {
  const cutoff = new Date();
  cutoff.setUTCDate(cutoff.getUTCDate() - days);
  const cutoffStr = isoDateUtc(cutoff);

  let clicks = 0;
  let impressions = 0;
  let positionWeighted = 0;

  for (const s of snapshots) {
    if (s.snapshot_date < cutoffStr) continue;
    clicks += s.metrics.clicks;
    impressions += s.metrics.impressions;
    positionWeighted += s.metrics.position * s.metrics.impressions;
  }

  const ctr = impressions > 0 ? clicks / impressions : 0;
  const position = impressions > 0 ? positionWeighted / impressions : 0;

  return {
    clicks,
    impressions,
    ctr,
    position,
    top_queries: [],
  };
}

/** Top queries over the last N days (merged by query text, max 10). */
export function mergeTopQueriesLastDays(
  snapshots: GscSnapshot[],
  days: number,
): GscMetrics['top_queries'] {
  const cutoff = new Date();
  cutoff.setUTCDate(cutoff.getUTCDate() - days);
  const cutoffStr = isoDateUtc(cutoff);

  const byQuery = new Map<string, { clicks: number; impressions: number }>();

  for (const s of snapshots) {
    if (s.snapshot_date < cutoffStr) continue;
    for (const row of s.metrics.top_queries) {
      const prev = byQuery.get(row.query) ?? { clicks: 0, impressions: 0 };
      byQuery.set(row.query, {
        clicks: prev.clicks + row.clicks,
        impressions: prev.impressions + row.impressions,
      });
    }
  }

  return [...byQuery.entries()]
    .map(([query, stats]) => ({ query, ...stats }))
    .sort((a, b) => b.clicks - a.clicks)
    .slice(0, 10);
}
